<template>
    <div class="fixed z-[9999] inset-0 pointer-events-none">
        <canvas 
            ref="canvas" 
            class="block w-full h-full !text-xl"
            :style="{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }"
        ></canvas>
    </div>
</template>

<script>
export default {
    data() {
        return {
            ctx: null,
            particles: [],
            animationId: null,
            w: 0,
            h: 0,
            snowColor: "rgb(180, 208, 255)",
            maxSize: 6,
            minSize: 3,
            maxSpeed: 3,
            minSpeed: 0.2,
            particleCount: 70,
        };
    },
    mounted() {
        if (this.$root.isMobile()) {
            this.particleCount = 15
        }
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }

        this.$nextTick(() => {
            this.observeParent();
        });

        window.addEventListener('resize', this.handleResize);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
        }
    },
    watch: {
        // Re-create particles if count changes
        particleCount() {
            this.createParticles();
        }
    },
    methods: {
        observeParent() {
            const canvas = this.$refs.canvas;
            if (!canvas || !canvas.parentElement) return;

            // Use ResizeObserver to detect when the parent size changes/stabilizes
            this.resizeObserver = new ResizeObserver(entries => {
                // ResizeObserver provides the size in the entries array
                for (let entry of entries) {
                    // Use contentRect for the content box size
                    const { width, height } = entry.contentRect;

                    if (this.w !== width || this.h !== height) {
                        this.w = width;
                        this.h = height;
                        
                        // Only run initialization if dimensions are valid
                        if (width > 0 && height > 0) {
                             this.initAndStart();
                        }
                    }
                }
            });

            this.resizeObserver.observe(canvas.parentElement);
        },
        initAndStart() {
            this.initCanvas();
            this.createParticles();
            
            // Only start the animation loop if it's not already running
            if (!this.animationId) {
                this.animate();
            }
        },
        initCanvas() {
            const canvas = this.$refs.canvas;
            this.ctx = canvas.getContext('2d');
            this.w = canvas.width = canvas.parentElement.clientWidth;
            this.h = canvas.height = canvas.parentElement.clientHeight;
        },
        handleResize() {
            const canvas = this.$refs.canvas;
            // Double check canvas exists before accessing
            if (!canvas) return; 
            
            this.w = canvas.width = canvas.parentElement.clientWidth;
            this.h = canvas.height = canvas.parentElement.clientHeight;
        },
        createParticles() {
            this.particles = [];
            for (let i = 0; i < this.particleCount; i++) {
                this.particles.push({
                    x: Math.random() * this.w,
                    y: Math.random() * this.h,
                    r: Math.random() * (this.maxSize - this.minSize) + this.minSize, // Used as size base
                    d: Math.random() * this.particleCount, // density/sway factor
                    s: Math.random() * (this.maxSpeed - this.minSpeed) + this.minSpeed // speed
                });
            }
        },
        draw() {
            this.ctx.clearRect(0, 0, this.w, this.h);
            this.ctx.fillStyle = this.snowColor;
            this.ctx.textAlign = "center";
            this.ctx.textBaseline = "middle";
            
            for (let i = 0; i < this.particleCount; i++) {
                const p = this.particles[i];
                // Multiplier creates a reasonable font size from the size props
                // 'lighter' font-weight makes it look less thick
                const fontSize = Math.floor(p.r * 5); 
                this.ctx.font = `lighter ${fontSize}px sans-serif`;
                this.ctx.fillText('*', p.x, p.y);
            }
            this.update();
        },
        update() {
            for (let i = 0; i < this.particleCount; i++) {
                const p = this.particles[i];
                
                // Move down
                p.y += p.s;
                // Sway slightly based on density
                p.x += Math.sin(p.d) * 0.5;
                p.d += 0.01;

                // Wrap around if it hits the bottom
                if (p.y > this.h) {
                    p.x = Math.random() * this.w;
                    p.y = -10; // Start slightly above
                }
                
                // Wrap around if it sways off the sides
                if (p.x > this.w + 5 || p.x < -5) {
                    if (Math.sin(p.d) > 0) {
                        p.x = -5;
                    } else {
                        p.x = this.w + 5;
                    }
                }
            }
        },
        animate() {
            this.draw();
            this.animationId = requestAnimationFrame(this.animate);
        }
    }
}

</script>